import {
  isFTL,
  MODE,
  TRUCK_TYPE,
  truckType
} from 'straightline-utils/constants';
import { yup } from 'straightline-utils/validation';
import {
  accessorialCodeSchema,
  addressSchema,
  lineItemSchema
} from 'straightline-utils/validation/schemas';

export default (app) => {
  return {
    schema: yup.object({
      truck_type: yup
        .string()
        .oneOf(Object.values(TRUCK_TYPE))
        .test({
          name: 'internationalTruckType',
          exclusive: true,
          message: 'Truck type not available for international shipments',
          test: function (truck_type, ctx) {
            if (!truck_type) {
              return true;
            }

            const pickup_address = this.resolve(
              yup.ref('pickup_address.country')
            );
            const delivery_address = this.resolve(
              yup.ref('delivery_address.country')
            );

            if (
              pickup_address &&
              delivery_address &&
              pickup_address !== delivery_address
            ) {
              const truck = truckType(truck_type);
              const internationalTrucks = [
                TRUCK_TYPE.DRY_VAN,
                TRUCK_TYPE.FLATBED
              ];
              if (truck && !internationalTrucks.includes(truck_type)) {
                return ctx.createError({
                  message: `${truck.label} is not available for international shipments`
                });
              }
            }

            return true;
          }
        })
        .label('Truck Type')
        .required(),
      pickup_address: addressSchema.required().label('Pickup Address'),
      delivery_address: addressSchema.required().label('Delivery Address'),
      pickup_date: yup
        .string()
        .dateOnly()
        .dateOnlyMin(() => new Date())
        .label('Pickup Date')
        .required(),
      due_date: yup
        .string()
        .dateOnly()
        .test({
          name: 'sameDay',
          exclusive: true,
          message: 'Invalid Date',
          test: function (due_date, ctx) {
            const pickup_date = this.resolve(yup.ref('pickup_date'));
            const mode = this.resolve(yup.ref('mode'));

            if (!due_date || !pickup_date || !mode) {
              return true;
            }

            if (!isFTL(mode) && due_date === pickup_date) {
              return ctx.createError({
                message:
                  'Same day delivery is not available for LTL and Parcel shipments'
              });
            }

            if (new Date(due_date) < new Date(pickup_date)) {
              return ctx.createError({
                message: 'Delivery Date must be after Pickup Date'
              });
            }

            return true;
          }
        })
        .label('Delivery Date')
        .required(),
      mode: yup
        .string()
        .trim()
        .oneOf(Object.values(MODE))
        .label('Mode')
        .required(),
      accessorial_codes: yup
        .array()
        .nullable()
        .label('Accessorial Codes')
        .min(1)
        .uniqueValues()
        .of(accessorialCodeSchema),
      line_items: yup
        .array()
        .min(1)
        .of(lineItemSchema)
        .label('Line Items')
        .required(),
      load_weight: yup
        .number()
        .positive()
        .integer()
        .round()
        .required()
        .when('truck_type', (truck_type, schema) => {
          if (!truck_type) {
            return schema;
          }
          const truck = truckType(truck_type);
          return schema.max(
            truck.max_load_weight,
            `Load Weight must be less than ${truck.max_load_weight} for ${truck.label} truck.`
          );
        })
        .label('Load Weight'),
      reference_number: yup
        .string()
        .trim()
        .transform((value) => {
          console.log('VALUE:', value);
          if (!value || value.length <= 255) {
            return value;
          }
          return `${value.slice(0, 252)}...`;
        })
        .nullable()
        .label('Reference Number')
    }),
    acceptSchema: yup.object({
      notes: yup.string().trim().nullable().label('Notes'),
      delivery_address: yup
        .object({
          appointment: yup
            .string()
            .trim()
            .label('Delivery Appointment')
            .required(),
          contact: yup
            .string()
            .trim()
            .label('Delivery Contact Name')
            .required(),
          email: yup.string().trim().email().label('Delivery Email'),
          phone: yup.string().trim().phone().label('Delivery Phone')
        })
        .atLeastOneRequired(['email', 'phone'], 'Phone or Email is required')
        .required()
        .label('Delivery Address'),
      pickup_address: yup
        .object({
          appointment: yup
            .string()
            .trim()
            .label('Pickup Appointment')
            .required(),
          contact: yup.string().trim().label('Pickup Contact Name').required(),
          email: yup.string().trim().email().label('Pickup Email'),
          phone: yup.string().trim().phone().label('Pickup Phone')
        })
        .atLeastOneRequired(['email', 'phone'], 'Phone or Email is required')
        .required()
        .label('Pickup Address')
    })
  };
};
